import { useCallback, useEffect, useMemo, useState } from "react";
import { normalizeChecklistPayload, reorderQuestionsByIndex } from "../utils/preStartChecklistV3.utils";

const normalizeEditorHtml = (html) => {
  const htmlValue = html ? `${html}` : "";
  const normalizedHtml = htmlValue
    .replace(/&nbsp;/gi, " ")
    .replace(/<br\s*\/?>(\n)?/gi, "")
    .replace(/<[^>]+>/g, "")
    .trim();

  if (!normalizedHtml) {
    return "";
  }

  return htmlValue;
};

const getChecklistQuestions = (checklist = {}) => {
  return checklist
    && checklist.data
    && Array.isArray(checklist.data.pre_start_questions)
    ? checklist.data.pre_start_questions
    : [];
};

const getChecklistHeading = (checklist = {}) => {
  return normalizeEditorHtml(checklist
    && checklist.data
    && typeof checklist.data.prestart_checklist_heading === "string"
    ? checklist.data.prestart_checklist_heading
    : "");
};

const getChecklistStaffSignoffHeading = (checklist = {}) => {
  return normalizeEditorHtml(checklist
    && checklist.data
    && typeof checklist.data.prestart_staff_signoff === "string"
    ? checklist.data.prestart_staff_signoff
    : "");
};

export default function usePreStartQuestionDraft({ swmsId, initialChecklist }) {
  const [savedPreStartChecklist, setSavedPreStartChecklist] = useState(() => normalizeChecklistPayload(initialChecklist));
  const [workingPreStartChecklist, setWorkingPreStartChecklist] = useState(() => normalizeChecklistPayload(initialChecklist));

  useEffect(() => {
    const normalizedChecklist = normalizeChecklistPayload(initialChecklist);
    setSavedPreStartChecklist(normalizedChecklist);
    setWorkingPreStartChecklist(normalizedChecklist);
  }, [swmsId, initialChecklist]);

  const hasUnsavedQuestionChanges = useMemo(() => {
    const savedQuestions = getChecklistQuestions(savedPreStartChecklist);
    const workingQuestions = getChecklistQuestions(workingPreStartChecklist);
    const savedHeading = getChecklistHeading(savedPreStartChecklist);
    const workingHeading = getChecklistHeading(workingPreStartChecklist);
    const savedStaffSignoffHeading = getChecklistStaffSignoffHeading(savedPreStartChecklist);
    const workingStaffSignoffHeading = getChecklistStaffSignoffHeading(workingPreStartChecklist);

    return JSON.stringify(savedQuestions) !== JSON.stringify(workingQuestions)
      || savedHeading !== workingHeading
      || savedStaffSignoffHeading !== workingStaffSignoffHeading;
  }, [savedPreStartChecklist, workingPreStartChecklist]);

  const questionCount = useMemo(() => getChecklistQuestions(workingPreStartChecklist).length, [workingPreStartChecklist]);

  const selectedQuestionByIndex = useCallback((index) => {
    if (index === null || index === undefined) {
      return null;
    }

    return getChecklistQuestions(workingPreStartChecklist)[index] || null;
  }, [workingPreStartChecklist]);

  const resetWorkingToSaved = useCallback(({ disabled = false } = {}) => {
    if (disabled || !hasUnsavedQuestionChanges) {
      return;
    }

    setWorkingPreStartChecklist(normalizeChecklistPayload(savedPreStartChecklist));
  }, [hasUnsavedQuestionChanges, savedPreStartChecklist]);

  const replaceDraftWithServerChecklist = useCallback((checklist = {}) => {
    const normalizedChecklist = normalizeChecklistPayload(checklist);
    setSavedPreStartChecklist(normalizedChecklist);
    setWorkingPreStartChecklist(normalizedChecklist);

    return normalizedChecklist;
  }, []);

  const applyToggleServerChecklist = useCallback((checklist = {}) => {
    const normalizedChecklist = normalizeChecklistPayload(checklist);

    setSavedPreStartChecklist(normalizedChecklist);
    setWorkingPreStartChecklist((prevChecklist) => {
      const localQuestions = getChecklistQuestions(prevChecklist);

      return {
        ...normalizedChecklist,
        data: {
          ...normalizedChecklist.data,
          pre_start_questions: localQuestions.length > 0 ? localQuestions : getChecklistQuestions(normalizedChecklist)
        }
      };
    });

    return normalizedChecklist;
  }, []);

  const moveQuestionRow = useCallback((fromIndex, toIndex, { disabled = false } = {}) => {
    if (disabled || fromIndex === toIndex) {
      return;
    }

    setWorkingPreStartChecklist((prevChecklist = {}) => {
      const previousData = prevChecklist && prevChecklist.data && typeof prevChecklist.data === "object"
        ? prevChecklist.data
        : {};
      const previousQuestions = Array.isArray(previousData.pre_start_questions)
        ? previousData.pre_start_questions
        : [];

      if (fromIndex < 0 || toIndex < 0 || fromIndex >= previousQuestions.length || toIndex >= previousQuestions.length) {
        return prevChecklist;
      }

      return {
        ...prevChecklist,
        data: {
          ...previousData,
          pre_start_questions: reorderQuestionsByIndex(previousQuestions, fromIndex, toIndex)
        }
      };
    });
  }, []);

  const onAfterDropQuestionRow = useCallback(({ from, to, didDrop }, { disabled = false } = {}) => {
    if (disabled || from === to) {
      return;
    }

    if (!didDrop) {
      setWorkingPreStartChecklist((prevChecklist = {}) => {
        const previousData = prevChecklist && prevChecklist.data && typeof prevChecklist.data === "object"
          ? prevChecklist.data
          : {};
        const previousQuestions = Array.isArray(previousData.pre_start_questions)
          ? previousData.pre_start_questions
          : [];

        if (to < 0 || from < 0 || to >= previousQuestions.length || from >= previousQuestions.length) {
          return prevChecklist;
        }

        return {
          ...prevChecklist,
          data: {
            ...previousData,
            pre_start_questions: reorderQuestionsByIndex(previousQuestions, to, from)
          }
        };
      });
    }
  }, []);

  const applyQuestionModalSubmit = useCallback((payload = {}) => {
    const {
      mode,
      selectedQuestion: selectedLocalQuestion,
      question
    } = payload;

    if (!question || (mode !== "add" && mode !== "edit")) {
      return;
    }

    setWorkingPreStartChecklist((prevChecklist = {}) => {
      const previousData = prevChecklist && prevChecklist.data && typeof prevChecklist.data === "object"
        ? prevChecklist.data
        : {};
      const previousQuestions = Array.isArray(previousData.pre_start_questions)
        ? previousData.pre_start_questions
        : [];

      if (mode === "edit") {
        const targetId = selectedLocalQuestion && selectedLocalQuestion.id;
        const updatedQuestions = previousQuestions.map((item = {}) => {
          if (`${item.id}` !== `${targetId}`) {
            return item;
          }

          return {
            ...item,
            ...question,
            id: item.id,
            display_order: Object.prototype.hasOwnProperty.call(item, "display_order") ? item.display_order : (question.display_order || null)
          };
        });

        return {
          ...prevChecklist,
          data: {
            ...previousData,
            pre_start_questions: updatedQuestions
          }
        };
      }

      const appendedQuestion = {
        ...question,
        id: question && question.id ? question.id : Date.now(),
        display_order: previousQuestions.length + 1
      };

      return {
        ...prevChecklist,
        data: {
          ...previousData,
          pre_start_questions: [...previousQuestions, appendedQuestion]
        }
      };
    });
  }, []);

  const updateHeadingContent = useCallback((headingContent = "") => {
    setWorkingPreStartChecklist((prevChecklist = {}) => {
      const previousData = prevChecklist && prevChecklist.data && typeof prevChecklist.data === "object"
        ? prevChecklist.data
        : {};
      const normalizedHeadingContent = normalizeEditorHtml(headingContent);

      if (getChecklistHeading(prevChecklist) === normalizedHeadingContent) {
        return prevChecklist;
      }

      return {
        ...prevChecklist,
        data: {
          ...previousData,
          prestart_checklist_heading: normalizedHeadingContent
        }
      };
    });
  }, []);

  const updateStaffSignoffHeadingContent = useCallback((headingContent = "") => {
    setWorkingPreStartChecklist((prevChecklist = {}) => {
      const previousData = prevChecklist && prevChecklist.data && typeof prevChecklist.data === "object"
        ? prevChecklist.data
        : {};
      const normalizedHeadingContent = normalizeEditorHtml(headingContent);

      if (getChecklistStaffSignoffHeading(prevChecklist) === normalizedHeadingContent) {
        return prevChecklist;
      }

      return {
        ...prevChecklist,
        data: {
          ...previousData,
          prestart_staff_signoff: normalizedHeadingContent
        }
      };
    });
  }, []);

  return {
    savedPreStartChecklist,
    workingPreStartChecklist,
    hasUnsavedQuestionChanges,
    questionCount,
    selectedQuestionByIndex,
    resetWorkingToSaved,
    replaceDraftWithServerChecklist,
    applyToggleServerChecklist,
    moveQuestionRow,
    onAfterDropQuestionRow,
    applyQuestionModalSubmit,
    updateHeadingContent,
    updateStaffSignoffHeadingContent
  };
}
